import React from "react";
import { useNavigate } from "react-router-dom";
import { Snowflake, Droplets, ArrowRight, BookCheck } from "lucide-react";

const HERO_IMG = "/gallery/hero-bmw-x6.jpg";

const TILES = [
  {
    id: "klima",
    eyebrow: "Kiemelt",
    title: "Klíma javítás-töltés",
    subtitle: "R134a · R1234yf · szivárgásvizsgálat",
    accent: "#0082D2",
    glow: "rgba(0,130,210,0.5)",
    Icon: Snowflake,
    bg: "https://images.unsplash.com/photo-1623861397259-d2dadccfeed7?auto=format&fit=crop&w=1600&q=80",
    badge: "AC",
    href: "/klima",
    price: "14 900 Ft",
    priceNote: "-tól",
  },
  {
    id: "valto",
    eyebrow: "MotulEVO",
    title: "Automataváltó olajcsere",
    subtitle: "MotulEVO · ZF · Aisin · DSG",
    accent: "#E1132C",
    glow: "rgba(225,19,44,0.5)",
    Icon: Droplets,
    bg: "https://images.unsplash.com/photo-1599256630897-cb1c1ad60b1d?auto=format&fit=crop&w=1600&q=80",
    badge: "EVO",
    href: "/automatavalto-olajcsere",
    price: "39 900 Ft",
    priceNote: "-tól",
  },
];

export default function Hero() {
  const navigate = useNavigate();

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative pt-24 sm:pt-28 pb-16 sm:pb-20 overflow-hidden min-h-[88vh] flex items-center"
      data-testid="hero-section"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="BMW X6 az Autó-Szerelem műhely előtt — Q-Service Debrecen"
          className="w-full h-full object-cover object-center opacity-50"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(10,10,10,0.5) 0%, rgba(10,10,10,0.85) 65%, #0A0A0A 100%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7 fade-up">
          <div className="flex items-center gap-4 mb-8">
            <div className="m-stripe h-1 w-24" />
            <span
              className="text-[0.65rem] sm:text-[0.72rem] font-bold uppercase tracking-[0.2em] sm:tracking-[0.32em] text-zinc-300"
              style={{ fontFamily: "Barlow Condensed" }}
            >
              Q-Service Szakszerviz / Debrecen
            </span>
          </div>

          <h1
            className="font-black uppercase leading-[0.92] tracking-[-0.01em] text-white"
            style={{
              fontFamily: "Barlow Condensed",
              fontSize: "clamp(2.1rem, 7vw, 6.5rem)",
            }}
            data-testid="hero-headline"
          >
            Profi autószerviz
            <br />
            <span className="text-[#0082D2]">Debrecen</span>
            <br className="sm:hidden" />
            <span className="sm:ml-[0.25em]">szívében.</span>
          </h1>

          <p
            className="mt-7 max-w-2xl text-zinc-300/90 text-base sm:text-lg leading-relaxed"
            data-testid="hero-subheadline"
          >
            <span className="text-white font-semibold">Autó-Szerelem</span> –
            mert az autó a szerelmünk. Klímatöltés, automataváltó-olajcsere
            MotulEVO technológiával, gumiszerelés, centrírozás és teljes körű
            javítás a Q-Service hálózat tagjaként.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a
              href="#foglalas"
              onClick={scrollTo("foglalas")}
              className="btn-primary"
              data-testid="hero-cta-booking"
            >
              Időpontfoglalás
            </a>
            <a
              href="#szolgaltatasok"
              onClick={scrollTo("szolgaltatasok")}
              className="btn-outline"
              data-testid="hero-cta-services"
            >
              Szolgáltatásaink
            </a>
          </div>
        </div>

        {/* Right column — featured tiles aligned with headline + stats below */}
        <div
          className="lg:col-span-5 flex flex-col gap-3 sm:gap-4 fade-up"
          style={{ animationDelay: "0.15s" }}
          data-testid="feature-tiles"
        >
          {/* PREMIUM BMW BADGE — top of right column */}
          <button
            type="button"
            onClick={() => navigate("/bmw")}
            className="group relative overflow-hidden text-left bg-[#0A0A0A] border border-[#0082D2]/50 hover:border-[#0082D2] transition-colors"
            data-testid="hero-bmw-badge"
            aria-label="Hivatalos BMW online szervíz-vezetés — részletek"
          >
            <div
              className="absolute inset-0 opacity-30 group-hover:opacity-50 transition-opacity pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse at right, rgba(0,130,210,0.45) 0%, rgba(10,10,10,0) 65%)",
              }}
            />
            <div className="m-stripe absolute top-0 left-0 right-0 h-[2px]" />

            <div className="relative px-4 sm:px-5 py-3 flex items-center gap-3.5">
              <div className="relative shrink-0">
                <div className="absolute inset-0 bg-[#0082D2]/40 blur-md" aria-hidden="true" />
                <div className="relative w-10 h-10 flex items-center justify-center bg-[#0A0A0A] border border-[#0082D2]/60">
                  <BookCheck
                    className="w-5 h-5 text-[#0082D2]"
                    strokeWidth={1.6}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <div
                  className="text-[0.55rem] sm:text-[0.6rem] font-bold uppercase tracking-[0.24em] text-[#0082D2] leading-none mb-1"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  Hivatalos partner
                </div>
                <div
                  className="font-black uppercase text-white leading-tight"
                  style={{
                    fontFamily: "Barlow Condensed",
                    fontSize: "clamp(0.95rem, 1.4vw, 1.15rem)",
                  }}
                >
                  BMW online szervíz-vezetés
                </div>
                <div className="text-[0.65rem] text-zinc-400 mt-0.5 truncate">
                  Kulcsba is bejegyezve · digitális szervízkönyv
                </div>
              </div>

              <ArrowRight
                className="w-4 h-4 text-[#0082D2] group-hover:translate-x-1 transition-transform shrink-0"
                strokeWidth={2}
              />
            </div>
          </button>

          {TILES.map((t, idx) => {
            const Icon = t.Icon;
            return (
              <button
                key={t.id}
                onClick={() => navigate(t.href)}
                data-testid={`feature-tile-${t.id}`}
                className="group relative overflow-hidden text-left fade-up"
                style={{ animationDelay: `${0.2 + idx * 0.08}s` }}
                aria-label={`${t.title} — részletek`}
              >
                {/* Background image */}
                <div className="absolute inset-0">
                  <img
                    src={t.bg}
                    alt=""
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div
                    className="absolute inset-0"
                    style={{
                      background: `linear-gradient(120deg, ${t.accent}d9 0%, rgba(10,10,10,0.85) 60%, rgba(10,10,10,0.95) 100%)`,
                    }}
                  />
                  <div
                    className="absolute inset-0 opacity-[0.07]"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 16px)",
                    }}
                  />
                </div>

                <div
                  className="absolute inset-0 pointer-events-none transition-opacity duration-500 opacity-0 group-hover:opacity-100"
                  style={{ boxShadow: `inset 0 0 60px ${t.glow}` }}
                />

                <div className="m-stripe absolute top-0 left-0 right-0 h-[3px]" />

                {/* Watermark badge */}
                <span
                  className="absolute -right-2 top-1/2 -translate-y-1/2 text-[6.5rem] sm:text-[8rem] font-black uppercase leading-none text-white/[0.06] select-none pointer-events-none"
                  style={{ fontFamily: "Barlow Condensed" }}
                  aria-hidden="true"
                >
                  {t.badge}
                </span>

                <div className="relative z-10 px-5 sm:px-6 py-5 flex items-center gap-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center bg-white/10 border border-white/30 backdrop-blur shrink-0"
                    style={{ boxShadow: `0 0 24px ${t.glow}` }}
                  >
                    <Icon
                      className="w-6 h-6 text-white"
                      strokeWidth={1.6}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[0.6rem] font-bold uppercase tracking-[0.24em] text-white/65 mb-1"
                      style={{ fontFamily: "Barlow Condensed" }}
                    >
                      {t.eyebrow}
                    </div>
                    <h2
                      className="font-black uppercase text-white leading-[0.95] tracking-tight truncate"
                      style={{
                        fontFamily: "Barlow Condensed",
                        fontSize: "clamp(1.1rem, 1.8vw, 1.5rem)",
                      }}
                    >
                      {t.title}
                    </h2>
                    <p className="mt-1 text-[0.72rem] text-white/70 mono truncate">
                      {t.subtitle}
                    </p>
                    {t.price && (
                      <div
                        className="mt-2 inline-flex items-baseline gap-1.5 bg-white/95 text-[#0A0A0A] px-2.5 py-1"
                        data-testid={`feature-tile-${t.id}-price`}
                      >
                        <span
                          className="font-black tracking-tight leading-none"
                          style={{
                            fontFamily: "Barlow Condensed",
                            fontSize: "clamp(1.25rem, 2vw, 1.6rem)",
                          }}
                        >
                          {t.price}
                        </span>
                        {t.priceNote && (
                          <span
                            className="text-[0.62rem] font-bold uppercase tracking-[0.18em] leading-none text-zinc-600"
                            style={{ fontFamily: "Barlow Condensed" }}
                          >
                            {t.priceNote}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <ArrowRight
                    className="w-5 h-5 text-white/80 transition-transform duration-300 group-hover:translate-x-1.5 shrink-0"
                    strokeWidth={2}
                  />
                </div>
              </button>
            );
          })}

          {/* Stats below tiles */}
          <div className="mt-6 grid grid-cols-3 gap-x-6 gap-y-4 fade-up" style={{ animationDelay: "0.4s" }}>
            <Stat number="32" label="Év tapasztalat" />
            <Stat number="5000+" label="Javított autó" />
            <Stat number="Q-Service" label="Hálózati tag" small />
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label, small }) {
  return (
    <div className="border-l-2 border-[#0082D2] pl-3">
      <div
        className={`font-black text-white tracking-tight leading-none ${
          small ? "text-lg sm:text-xl" : "text-2xl sm:text-3xl"
        }`}
        style={{ fontFamily: "Barlow Condensed" }}
      >
        {number}
      </div>
      <div className="text-[0.6rem] font-bold uppercase tracking-[0.16em] text-zinc-400 mt-1.5 leading-tight">
        {label}
      </div>
    </div>
  );
}
